/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { NANOS_OF_DAY } from './types';

// Time utilities

const NANOSECONDS_PER_MILLISECOND = 1000000n;
const NANOSECONDS_PER_SECOND = 1000000000n;
const NANOSECONDS_PER_MINUTE = 60000000000n;
const NANOSECONDS_PER_HOUR = 3600000000000n;
const NANOSECONDS_PER_DAY = 86400000000000n;

/* @internal */ export { NANOSECONDS_PER_DAY, NANOSECONDS_PER_HOUR, NANOSECONDS_PER_MINUTE, NANOSECONDS_PER_SECOND, NANOSECONDS_PER_MILLISECOND }

// https://tc39.github.io/ecma262/#sec-maketime
/* @internal */ export function getTime(hours: number, minutes: number, seconds: number, milliseconds: number, nanoseconds: number) {
    const time =
        BigInt(hours) * NANOSECONDS_PER_HOUR +
        BigInt(minutes) * NANOSECONDS_PER_MINUTE +
        BigInt(seconds) * NANOSECONDS_PER_SECOND +
        BigInt(milliseconds) * NANOSECONDS_PER_MILLISECOND +
        BigInt(nanoseconds);
    return time as NANOS_OF_DAY;
}

/* @internal */ export interface TimeParts {
    overflowDays: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
    nanosecond: number;
}

/* @internal */ export const enum TimePart {
    overflowDays,
    hour,
    minute,
    second,
    millisecond,
    nanosecond,
    all
}

/* @internal */ export function getTimePart<P extends TimePart>(time: NANOS_OF_DAY, part: P): P extends TimePart.all ? TimeParts : number;
/* @internal */ export function getTimePart(time: NANOS_OF_DAY, part: TimePart) {
    let n: bigint = time;
    const overflowDaysPart = n / NANOSECONDS_PER_DAY;
    if (part === TimePart.overflowDays) return Number(overflowDaysPart);
    n = n % NANOSECONDS_PER_DAY;
    const hourPart = n / NANOSECONDS_PER_HOUR;
    if (part === TimePart.hour) return Number(hourPart);
    n = n % NANOSECONDS_PER_HOUR;
    const minutePart = n / NANOSECONDS_PER_MINUTE;
    if (part === TimePart.minute) return Number(minutePart);
    n = n % NANOSECONDS_PER_MINUTE;
    const secondPart = n / NANOSECONDS_PER_SECOND;
    if (part === TimePart.second) return Number(secondPart);
    n = n % NANOSECONDS_PER_SECOND;
    const millisecondPart = n / NANOSECONDS_PER_MILLISECOND;
    if (part === TimePart.millisecond) return Number(millisecondPart);
    n = n % NANOSECONDS_PER_MILLISECOND;
    if (part === TimePart.nanosecond) return Number(n);
    const overflowDays = Number(overflowDaysPart);
    const hour = Number(hourPart);
    const minute = Number(minutePart);
    const second = Number(secondPart);
    const millisecond = Number(millisecondPart);
    const nanosecond = Number(n);
    return { overflowDays, hour, minute, second, millisecond, nanosecond };
}


